"use client";

import React, { useState } from "react";
import { ArrowRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Introduction() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [currentImage, setCurrentImage] = useState(0);
  const carouselImages = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587884/img/tai_chi_fan.png",
    alt: "Tai Chi Fächer"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587884/img/tai_chi_gan_dao.png",
    alt: "Tai Chi Gan Dao"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587884/img/tai_chi_ball.png",
    alt: "Tai Chi Ball"
  }];
  const nextImage = () => setCurrentImage(prev => (prev + 1) % carouselImages.length);
  const prevImage = () => setCurrentImage(prev => (prev - 1 + carouselImages.length) % carouselImages.length);
  const benefits = ["Verbesserung der Körperhaltung und Balance", "Förderung der mentalen Fokussierung", "Stärkung der tiefliegenden Muskulatur", "Stressabbau durch meditative Bewegung"];
  return <section id="einführung" className="bg-background py-20 md:py-32 overflow-hidden font-['Montserrat']">
      <div className="container mx-auto px-6 md:px-12 lg:px-20 max-w-[1200px]">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center mb-32">
          
          <div ref={textRef} className={`flex flex-col items-start gap-6 md:gap-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="text-4xl md:text-5xl lg:text-[3rem] font-light text-[#2C5354] leading-[1.1] tracking-[-0.02em] uppercase">Die Kunst der fließenden Kraft</h2>
            
            <div className="flex flex-col gap-8">
              <div>
                <h3 className="text-xl font-normal uppercase tracking-widest text-[#2C5354] mb-3">Was ist Tai Chi?</h3>
                <p className="text-lg text-muted-foreground leading-[1.8] font-light max-w-2xl">Die Wurzeln des Tai Chi reichen mehr als 800 Jahre zurück. Eine der bekanntesten Legenden schreibt die Entstehung dem daoistischen Mönch Zhang Sanfeng zu. Er soll beobachtet haben, wie eine Schlange einem Kranich auswich: Durch ihre geschmeidigen, nachgiebigen Bewegungen besiegte sie die harten Attacken des Vogels. Inspiriert von diesem Sieg der Weichheit über die Härte, entwickelte er Tai Chi, wie es noch heute in den Wudangbergen Chinas gelehrt wird.</p>
              </div>

              <div>
                <h3 className="text-xl font-normal uppercase tracking-widest text-[#2C5354] mb-3">
                  Die Legende: Von Schlange und Kranich
                </h3>
                <p className="text-lg text-muted-foreground leading-[1.8] font-light max-w-2xl">Die Wurzeln des Tai Chi reichen mehr als 800 Jahre zurück. Eine der bekanntesten Legenden schreibt die Entstehung dem daoistischen Mönch Zhang Sanfeng zu. Er soll beobachtet haben, wie eine Schlange einem Kranich auswich: Durch ihre geschmeidigen, nachgiebigen Bewegungen besiegte sie die harten Attacken des Vogels. Inspiriert von diesem Sieg der Weichheit über die Härte, entwickelte er Tai Chi, wie es noch heute in den Wudangbergen Chinas praktiziert und gelehrt wird.</p>
              </div>
            </div>
          </div>

          <div ref={imageRef} className={`relative w-full aspect-[4/5] md:aspect-square lg:aspect-[4/5] rounded-xl overflow-hidden shadow-xl transition-all duration-1000 delay-200 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="relative w-full h-full">
              {carouselImages.map((img, idx) => <div key={idx} data-index={idx} className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${idx === currentImage ? "opacity-100" : "opacity-0"}`}>
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
                </div>)}
              
              <div className="absolute inset-0 flex items-center justify-between px-4 z-20 !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587884/img/crane_and_snake.png)] bg-cover bg-center !bg-[url(https://huge-sunbird.10web.cloud/wp-content/uploads/2026/04/image_v3mba3k4.jpg)] !bg-[url(https://huge-sunbird.10web.cloud/wp-content/uploads/2026/04/image_v3mba3k4.jpg)]">
              </div>

              <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 w-max max-w-[90%] z-10">
                <Badge variant="secondary" className="bg-background/95 backdrop-blur-md text-foreground uppercase tracking-[0.05em] text-xs font-normal py-2 px-5 md:py-2.5 md:px-6 rounded-full shadow-lg border border-border/50">
                  {carouselImages[currentImage].alt}
                </Badge>
              </div>
            </div>
            
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
          </div>

        </div>

        <div className="space-y-24 max-w-4xl mx-auto lg:mx-0">
          <div id="die-legende">
            <h3 className="text-xl font-normal uppercase tracking-widest text-[#2C5354] mb-3">Die Stile und der Wandel der Zeit</h3>
            <p className="text-lg text-muted-foreground leading-[1.8] font-light">Aus diesen Wurzeln formten sich im Laufe der Jahrhunderte die prägenden Familienstile mit ihren individuellen Charakteristika: Der Chen-Stil bewahrte dynamische Spiralkräfte und den Wechsel zwischen Sanftheit und kraftvollem Ausdruck. Daraus entwickelte sich der Yang-Stil, der für seine weiträumigen, sanften und gleichmäßigen Bewegungen bekannt ist. Jüngere Linien wie der Wu-Stil und der Sun-Stil brachten kompaktere Haltungen, wendige Schritte und spezifische Impulse ein. Trotz ihrer unterschiedlichen Äußerlichkeiten teilen alle Stile dieselben grundlegenden Prinzipien.</p>
          </div>

          <div className="h-px w-full bg-[#2C5354]/20" />

          <div id="tai-chi-im-modernen-china">
            <h3 className="text-xl font-normal uppercase tracking-widest text-[#2C5354] mb-3">Tai Chi im modernen China</h3>
            <p className="text-lg text-muted-foreground leading-[1.8] font-light">In den letzten 50 Jahren hat Tai Chi eine enorme Transformation durchlaufen. In China wurde es staatlich gefördert und standardisiert, um die Volksgesundheit zu stärken. Die berühmte 24er Peking-Form entstand 1956 genau aus diesem Grund: Um die komplexen Familienstile zu vereinfachen und Tai Chi für Millionen von Menschen zugänglich zu machen. Heutzutage entwickeln Sportwissenschaftler moderne Formen des Tai Chi für Menschen mit wenig Zeit und Platz. Die traditionellen Linien werden jedoch ebenfalls noch von Meister zu Schüler*in weitergegeben.&nbsp;</p>
          </div>

          <div id="die-waffen">
          </div>
        </div>
      </div>
    </section>;
}